<template>
    <div class="profile">
        <div class="profile__image-box">
            <img :src="image.avatar" alt="avatar" class="profile__image">
            <span class="profile__name" v-if="contacts">{{contacts.fullname}}</span>
            <span class="profile__role" v-if="role">{{role}}</span>
            <button-component class="profile__add-photo" button-text="Загрузить фото" icon-name="cloud" icon-size="24" />
        </div>
        <div class="profile__information-box" v-if="contacts">
            <ul class="profile__information">
                <li class="profile__item">
                    <span class="profile__text">Полное имя</span>
                    <form method="post" class="profile__form" v-if="editing === 'fullname'">
                        <input-component v-model:meaning="fullname" inputContent="Полное имя" inputName="fullname" inputType="text"/>
                        <div class="profile__edit-box">
                            <button-component class="profile__edit" iconName="success" iconSize="32" @click.prevent="updateFullnameHandler"/>
                            <button-component class="profile__edit" iconName="closeBig" iconSize="32" @click="toggleEditing(null)" />
                        </div>
                    </form>
                    <div class="profile__text-box" v-else >
                        <span class="profile__text profile__text-value">{{contacts.fullname}}</span>
                        <button-component class="profile__editing" iconName="pencil" iconSize="22" @click="toggleEditing('fullname')" />
                    </div>
                </li>
                <li class="profile__item">
                    <span class="profile__text">Email для связи</span>
                    <form method="post" class="profile__form" v-if="editing === 'email'">
                        <input-component v-model:meaning="email" inputContent="Email для связи" inputName="email" inputType="text" v-maska="emailMaskOptions"/>
                        <div class="profile__edit-box">
                            <button-component class="profile__edit" iconName="success" iconSize="32" @click.prevent="updateEmailHandler"/>
                            <button-component class="profile__edit" iconName="closeBig" iconSize="32" @click="toggleEditing(null)" />
                        </div>
                    </form>
                    <div class="profile__text-box" v-else >
                        <span class="profile__text profile__text-value">{{contacts.email}}</span>
                        <button-component class="profile__editing" iconName="pencil" iconSize="22" @click="toggleEditing('email')"/>
                    </div>
                </li>
                <li class="profile__item">
                    <span class="profile__text">Номер телефона</span>
                    <form method="post" class="profile__form" v-if="editing === 'phone'">
                        <input-component v-model:meaning="phone" inputContent="Номер телефона" inputName="phone" inputType="text" v-maska="'+7 ### ###-##-##'"/>
                        <div class="profile__edit-box">
                            <button-component class="profile__edit" iconName="success" iconSize="32" @click.prevent="updatePhoneHandler"/>
                            <button-component class="profile__edit" iconName="closeBig" iconSize="32" @click="toggleEditing(null)" />
                        </div>
                    </form>
                    <div class="profile__text-box" v-else >
                        <span class="profile__text profile__text-value">{{contacts.phone}}</span>
                        <button-component class="profile__editing" iconName="pencil" iconSize="22" @click="toggleEditing('phone')" />
                    </div>
                </li>
                <li class="profile__item">
                    <span class="profile__text">Дата рождения</span>
                    <form method="post" class="profile__form" v-if="editing === 'birthday'">
                        <input-component v-model:meaning="birthday" inputContent="Дата рождения" inputName="birthday" inputType="text" v-maska="orderDateMask"/>
                        <div class="profile__edit-box">
                            <button-component class="profile__edit" iconName="success" iconSize="32" @click.prevent="updateBirthdayHandler"/>
                            <button-component class="profile__edit" iconName="closeBig" iconSize="32" @click="toggleEditing(null)" />
                        </div>
                    </form>
                    <div class="profile__text-box" v-else >
                        <span class="profile__text profile__text-value">{{contacts.birthday}}</span>
                        <button-component class="profile__editing" iconName="pencil" iconSize="22" @click="toggleEditing('birthday')" />
                    </div>
                </li>
                <li class="profile__item">
                    <span class="profile__text">Роль</span>
                    <form method="post" class="profile__form">
                        <label class="radio">
                            <input type="radio" name="role" value="user">
                            Пользователь
                        </label>
                        <label class="radio">
                            <input type="radio" name="role" value="manager">
                            Менеджер
                        </label>
                        <label class="radio">
                            <input type="radio" name="role" value="chief_manager">
                            Главный менеджер
                        </label>
                        <label class="radio">
                            <input type="radio" name="role" value="admin">
                            Администратор
                        </label>
                    </form>
                </li>
            </ul>
        </div>
    </div>
</template>
<script setup lang="ts">
import buttonComponent from '../shared/buttonComponent.vue'
import inputComponent from '../shared/Form/Input/InputComponent.vue'
import axios from '../app/lib/api'
import { Contacts, Field } from '../app/lib/types/users'
import avatar from '../assets/img/avatar.png'
import {onMounted, reactive, Ref, ref} from 'vue'
import { updateContactsBirthdayShema, updateContactsEmailShema, updateContactsFullnameShema, updateContactsPhoneShema, updateContactsType } from '../app/lib/shemas/updateUserShema'
import toast from '../app/lib/hooks/errorToast'
import { useLoadingStore } from '../app/store/store'
import { vMaska } from 'maska/vue'
import { MaskInputOptions } from "maska"

const image = {
    avatar: avatar
}
let staticContacts = {} as Contacts
let contacts = ref({} as Contacts)
const role = localStorage.getItem("role")
const fullname = ref("")
const email = ref("")
const phone = ref("")
const birthday = ref("")
const editing: Ref<Field | null> = ref(null)
const loading = useLoadingStore()
const emailMaskOptions = reactive<MaskInputOptions>({
    mask: "A!@A.Z",
    tokens: {
        A: {
            pattern: /[A-Za-z0-9]/,
            multiple: true
        },
        Z: {
            pattern: /[a-z]/,
            multiple: true
        }
    }
})
const orderDateMask = reactive<MaskInputOptions>({
    mask: 'AA.AA.AAAA',
    tokens: {
        A: {
            pattern: /\d/
        }
    }
})

function toggleEditing(item: Field | null) {
    editing.value = item
    fullname.value = contacts.value.fullname
    email.value = contacts.value.email
    phone.value = contacts.value.phone
    birthday.value = contacts.value.birthday
}

async function updateFullnameHandler(){
    try {
        await updateContactsFullnameShema.validate(fullname.value, { abortEarly: false })
        try {
            loading.loading()
            const res = await axios.users.updateUserInfo({ fullname: fullname.value } as updateContactsType)
            contacts.value.fullname = fullname.value
            editing.value = null
            loading.load()
            toast("success", res.data.message)
        } catch (error: any) {
            loading.load()
            toast("error", error.response.data.message)
        }
    } catch (error: any) {
        toast("error", error.message)
    }
}

async function updateEmailHandler(){
    try {
        await updateContactsEmailShema.validate(email.value, { abortEarly: false })
        try {
            loading.loading()
            const res = await axios.users.updateUserInfo({ email: email.value } as updateContactsType)
            contacts.value.email = email.value
            editing.value = null
            loading.load()
            toast("success", res.data.message)
        } catch (error: any) {
            loading.load()
            toast("error", error.response.data.message)
        }
    } catch (error: any) {
        toast("error", error.message)
    }
}

async function updatePhoneHandler(){
    try {
        await updateContactsPhoneShema.validate(phone.value, { abortEarly: false })
        try {
            loading.loading()
            const res = await axios.users.updateUserInfo({ phone: phone.value } as updateContactsType)
            contacts.value.phone = phone.value
            editing.value = null
            loading.load()
            toast("success", res.data.message)
        } catch (error: any) {
            loading.load()
            toast("error", error.response.data.message)
        }
    } catch (error: any) {
        toast("error", error.message)
    }
}

async function updateBirthdayHandler(){
    try {
        await updateContactsBirthdayShema.validate(birthday.value, { abortEarly: false })
        try {
            loading.loading()
            const res = await axios.users.updateUserInfo({ birthday: birthday.value } as updateContactsType)
            contacts.value.birthday = birthday.value
            editing.value = null
            loading.load()
            toast("success", res.data.message)
        } catch (error: any) {
            loading.load()
            toast("error", error.response.data.message)
        }
    } catch (error: any) {
        toast("error", error.message)
    }
}

onMounted(async () => {
    loading.loading()
    axios.users.getUserInfo()
        .then((res: any) => {
            contacts.value = res.data
            fullname.value = staticContacts.fullname
            email.value = staticContacts.email
            phone.value = staticContacts.phone
            birthday.value = staticContacts.birthday
            loading.load()
        })
        .catch((err: any) => {
            loading.load()
        })
})
</script>